import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import classnames from 'classnames';
import React from 'react';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { format } from 'src/lib/format';

import { ClientMetrics } from '../../useCurrentField';

interface Props {
  clientName: string;
  clientMetrics: ClientMetrics;
  onClick: (clientName: string) => void;
}

export const FieldPageClientMetric = ({
  clientName,
  clientMetrics,
  onClick,
}: Props) => {
  const { requests, executions, operationMetrics } = clientMetrics;
  const operations = operationMetrics ? operationMetrics.size : 0;
  let estimatedExecutions = 0;
  operationMetrics.forEach((value) => {
    estimatedExecutions += value.estimatedExecutions;
  });
  const displayedExecutionCount = estimatedExecutions ?? executions;
  const useExecutionMetrics = requests === 0 && executions > 0;

  const UnidentifiedClientName = () => (
    <div>
      <div className="flex items-center">
        <span className="text-secondary">Unidentified clients</span>
        <Tooltip
          className="w-[225px]"
          interactive
          label={
            <div className="font-normal">
              Learn how to set client names{' '}
              <ClickableText
                className="text-link hover:underline"
                as="a"
                rel="noopener noreferrer"
                target="_blank"
                href="https://www.apollographql.com/docs/graphos/metrics/client-awareness/#setup"
              >
                here
              </ClickableText>
              .
            </div>
          }
        >
          <IconInfo className="ml-2 text-secondary" />
        </Tooltip>
      </div>
    </div>
  );

  return (
    <div
      className={classnames(
        'group w-full border-b border-b-primary bg-primary',
      )}
    >
      <div
        className="flex w-full cursor-pointer items-center py-4 pl-6 group-hover:bg-table-hover"
        onClick={() => onClick(clientName)}
        data-analytics-category="Field Insights"
        data-analytics-label="Client name click"
      >
        <div className="min-w-0 flex-1 items-center">
          <h4 className="truncate text-xl text-primary">
            {clientName || <UnidentifiedClientName />}
          </h4>
          <p className="text-lg text-secondary">
            {format.number(operations, '0[.]0a')}{' '}
            {format.pluralize(operations, 'operation')}
          </p>
        </div>
        {useExecutionMetrics ? (
          <div className="text-right">
            <h3 className="text-2xl text-primary">
              {format.number(displayedExecutionCount, '0[.]0a')}
            </h3>
            <p className="text-sm font-semibold uppercase text-secondary">
              {format.pluralize(displayedExecutionCount, 'execution')}
            </p>
          </div>
        ) : (
          <div className="text-right">
            <h3 className="text-2xl text-primary">
              {format.number(requests, '0[.]0a')}
            </h3>
            <p className="text-sm font-semibold uppercase text-secondary">
              {format.pluralize(requests, 'request')}
            </p>
          </div>
        )}
        <div className="flex h-full items-center px-6 text-secondary group-hover:text-primary">
          <IconChevronRight />
        </div>
      </div>
    </div>
  );
};
